import {  useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineEdit, AiOutlineSetting } from "react-icons/ai";
import DocumentUploadModal from "./DocumentUploadModal";
import type { Tenant } from "../../../shared/types";
import { UserAvatar } from "../../../shared/ui/UserAvatar";
import { getTenantById } from "../../../shared/services/apis";
import type { Document as TenantDocument } from "../../../shared/types";

interface TenantCardProps {
  tenant: Tenant;
  onDocumentUpload?: (tenantId: string) => void;
  onServiceManagement?: (tenantId: string, tenantName: string) => void;
  index?:number;
}

const TenantCard = ({
  tenant,
  onDocumentUpload,
  onServiceManagement,
 index

}: TenantCardProps) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);


const [documents, setDocuments] = useState<TenantDocument[]>(tenant.documents ?? []);



  const navigate = useNavigate();

  const handleCardClick = () => {
    // Navigate to the tenant detail page using the tenant ID
    navigate(`/tenant/${tenant.id}`, {
      state: {
        tenantId: tenant.id,
        tenantTitle: tenant.name,
        tenantData: tenant,
      },
    });
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUploadModalOpen(true);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      setIsUploadModalOpen(true);
    }
  };

  const handleServiceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onServiceManagement?.(tenant.id, tenant.name);
  };

  const handleServiceKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      onServiceManagement?.(tenant.id, tenant.name);
    }
  };

  // Check if tenant has any documents with IN_PROGRESS ingestion status
 const hasInProgressDocuments = documents.some(
  (doc) => doc.ingestionStatus === "IN_PROGRESS"
);

useEffect(() => {
  let interval: ReturnType<typeof setInterval> | null = null;

  if (hasInProgressDocuments) {
    interval = setInterval(async () => {
      try {
        const data = await getTenantById(tenant.id);
        setDocuments(data.documents ?? []);

        const stillInProgress = data.documents?.some(
          (doc: TenantDocument) => doc.ingestionStatus === "IN_PROGRESS"
        );

        if (!stillInProgress && interval) {
          clearInterval(interval);
        }
      } catch (err) {
        console.error("Error fetching documents:", err);
      }
    }, 5000);
  }

  return () => {
    if (interval) clearInterval(interval);
  };
}, [tenant.id, documents,hasInProgressDocuments]);


  const handleUploadSuccess = (newDocs: TenantDocument[]) => {
  setDocuments(newDocs);
  onDocumentUpload?.(tenant.id);
};


const gradients = [
  "bg-gradient-to-b from-[#0F294B]/95 via-[#0F294B]/90 via-[65%] to-[#236CB1]/85",
  "bg-gradient-to-b from-[#0F4B29]/95 via-[#0F4B29]/90 via-[65%] to-[#2FCDBD]/85",
  "bg-gradient-to-b from-[#382CA0]/95 via-[#382CA0]/90 via-[65%] to-[#985AAE]/85",
  "bg-gradient-to-b from-[#1C4BD9]/95 via-[#1C4BD9]/90 via-[65%] to-[#7ACEDE]/85",
  "bg-gradient-to-b from-[#4c1d1d]/95 via-[#7f1d1d]/90 via-[65%] to-[#991b1b]/85", 
  "bg-gradient-to-b from-[#3a245c]/95 via-[#55287c]/90 via-[65%] to-[#7e3fb2]/85",
  "bg-gradient-to-b from-[#4a1d35]/95 via-[#6b2147]/90 via-[65%] to-[#9d325f]/85",

];


const gradient = gradients[index !== undefined ? index % gradients.length : 0];



  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        className={`rounded-3xl overflow-hidden bg-gray-50  cursor-pointer relative group  transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          hasInProgressDocuments
            ? "ring-2 ring-amber-400 shadow-lg shadow-amber-100/50"
            : ""
        }`}
        aria-label={`View details for ${tenant.name}`}
      >
        <div
          className="rounded-3xl overflow-hidden bg-gray-50 max-w-22rem  border border-gray-200 cursor-pointer relative group  transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          aria-label={`View details for ${tenant.name}`}
        >
          {/* Book Cover */}
          <div
            className={`h-54 w-50 mx-8 md:mx-21 mt-10 rounded-tr-xl ${gradient} flex flex-col items-start justify-center relative  border-l-2 border-black/30
              before:content-[''] before:absolute before:top-0 before:bottom-0 before:left-4 before:w-[2px] before:bg-black/30 before:rounded-full before:shadow-[2px_0_4px_rgba(255,255,255,0.8)]`}
          >
            {/* Processing Indicator */}
            {hasInProgressDocuments && (
              <div className="absolute top-3 -right-16 bg-amber-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1.5 shadow-lg animate-pulse">
                <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping"></div>
                <span className="font-medium">Processing</span>
              </div>
            )}

            <div className="pl-8">
              <div>
                <img
                  src={tenant.logoUrl}
                  alt={tenant.name}
                  className="h-10 w-10 rounded-xl "
                />
                <h2 className="font-['Crimson_Pro'] text-white font-medium text-xl leading-[120%] tracking-[0] align-middle mt-4">
                  {tenant.name}
                </h2>
              </div>
              <p className="font-['Geist',_serif] text-white font-thin text-[11px] leading-[159%] tracking-[0.07px] mt-2 pr-1">
                A Comprehensive Analysis of {tenant.name} generated by AI
              </p>
            </div>
          </div>
        </div>
        {/* Author Info */}
        <div
          className={`flex bg-[#FDFDFD] items-center justify-between px-6 py-5 ${
            hasInProgressDocuments
              ? "bg-gradient-to-r from-amber-50/50 to-transparent"
              : ""
          }`}
        >
          <div className="flex items-center w-full gap-3 ">
            {/* <img
              src="https://i.pravatar.cc/39"
              alt="Profile"
              className="w-10 h-10 rounded-full object-cover"
            /> */}
            <UserAvatar
              name={tenant.createdBy?.fullName || "Unknown User"}
              imageUrl={""}
              size="sm"
            />
            <div>
              <div className="flex items-center w-full gap-2">
                <p className="text-gray-900 font-medium text-sm">
                  {tenant.createdBy?.fullName || "Unknown User"}
                </p>
                {hasInProgressDocuments && (
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                )}
              </div>
              <p className="text-xs text-gray-400">
                {new Date(tenant.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={handleServiceClick}
              onKeyDown={handleServiceKeyDown}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Manage Services"
              aria-label="Manage services"
            >
              <AiOutlineSetting className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={handleEditClick}
              onKeyDown={handleEditKeyDown}
              className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Upload Documents"
              aria-label="Upload documents"
            >
              <AiOutlineEdit className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        tenantId={tenant.id}
        tenantName={tenant.name}
        onUploadSuccess={handleUploadSuccess}
      />
    </>
  );
};

export default TenantCard;
